/**
 * OffersCarousel - Ofertas y Descuentos section for marketplace landing
 * White container on gray canvas, 2-row horizontal scroll grid
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tag, ChevronRight, Store } from 'lucide-react';
import { getOffers } from '../../services/api';
import { getProductImage } from '../../utils/constants';

const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
};

const OffersCarousel = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getOffers();
        setOffers(res.data);
      } catch (e) {
        console.error('Error loading offers:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 mt-4">
        <div className="bg-white rounded-lg shadow-sm p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-5">
            <Tag className="w-5 h-5 text-red-500" />
            <h2 className="text-lg sm:text-xl font-bold text-slate-800">Ofertas y Descuentos</h2>
          </div>
          <div className="grid grid-rows-2 grid-flow-col gap-3 overflow-hidden auto-cols-[160px]">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="w-40 h-52 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (offers.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 mt-4">
      <div className="bg-white rounded-lg shadow-sm p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-red-500" />
            <h2 className="text-lg sm:text-xl font-bold text-slate-800">Ofertas y Descuentos</h2>
          </div>
          <Link to="/buscar?sort=discount" className="flex items-center gap-1 text-sm font-medium hover:underline" style={{ color: COLORS.navyBlue }}>
            Ver todas <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div
          className="grid grid-rows-2 grid-flow-col gap-3 overflow-x-auto pb-2 scrollbar-hide"
          style={{ gridAutoColumns: '160px' }}
        >
          {offers.map(item => (
            <Link
              key={item.product_id}
              to={`/producto/${item.product_id}`}
              className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-md hover:bg-white transition-all group"
            >
              <div className="aspect-square bg-gray-50 relative overflow-hidden">
                <img
                  src={getProductImage(item.thumbnail_url || item.image_url)}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.target.src = getProductImage(); }}
                />
                <span className="absolute top-2 left-2 px-1.5 py-0.5 text-[11px] font-bold rounded-md bg-red-500 text-white">
                  -{item.discount_pct}%
                </span>
              </div>
              <div className="p-2">
                <h3 className="text-xs font-medium text-slate-800 line-clamp-2 leading-tight">{item.name}</h3>
                <div className="mt-1 flex items-center gap-1.5">
                  <span className="text-sm font-bold" style={{ color: COLORS.navyBlue }}>
                    ${item.retail_price.toFixed(2)}
                  </span>
                  <span className="text-xs text-slate-400 line-through">
                    ${item.compare_at_price.toFixed(2)}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
                  <Store className="w-2.5 h-2.5" />
                  <span className="truncate">{item.business_name}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OffersCarousel;
